import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
      {/* Background image with warm tones for text contrast */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20earth%20toned%20abstract%20texture%20with%20rich%20terracotta%20and%20deep%20sage%20gradients%2C%20organic%20flowing%20patterns%2C%20subtle%20dark%20tones%20at%20the%20top%20for%20navigation%20contrast%2C%20soft%20and%20minimal%20artistic%20wallpaper%2C%20editorial%20quality&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/10" />
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
          <div className="flex-1 max-w-2xl pt-8 md:pt-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100/60 text-accent-700 text-xs font-semibold mb-8 font-label tracking-wide">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              The Largest Directory
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-[1.08] mb-6">
              The Largest CSSBuy Spreadsheet Directory for 2026
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-lg mb-10">
              Discover thousands of verified fashion finds, sneakers, accessories, luxury items, and trusted sellers through the most comprehensive CSSBuy Spreadsheet online.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Explore Spreadsheet
                  <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl border border-background-200 shadow-lg overflow-hidden z-30 animate-[fadeIn_0.2s_ease-out]">
                    <div className="max-h-72 overflow-y-auto py-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </span>
                          <div>
                            <span className="font-medium block whitespace-nowrap">{cat.name}</span>
                            <span className="text-xs text-foreground-400">{cat.description}</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-full border-2 border-foreground-300 text-foreground-700 text-sm font-semibold hover:border-foreground-500 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Browse Latest Finds
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-5 mt-8">
              {[
                { icon: 'ri-check-line', text: 'Updated Daily' },
                { icon: 'ri-shield-check-line', text: 'Verified Sellers' },
                { icon: 'ri-guide-line', text: 'Shipping Guides' },
                { icon: 'ri-image-line', text: 'QC Tutorials' },
              ].map((badge) => (
                <span key={badge.text} className="flex items-center gap-1.5 text-xs text-foreground-500 font-medium">
                  <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                    <i className={badge.icon}></i>
                  </span>
                  {badge.text}
                </span>
              ))}
            </div>
          </div>

          <div className="flex-1 w-full flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Fashion%20collage%20of%20stylish%20sneakers%20and%20streetwear%20clothing%20items%20arranged%20artistically%20on%20a%20warm%20cream%20background%2C%20minimalist%20editorial%20style%2C%20soft%20natural%20lighting%2C%20clean%20composition%20with%20designer%20shoes%20hoodies%20and%20accessories%2C%20high%20fashion%20aesthetic%20with%20earthy%20tones&width=800&height=1000&seq=hero-fashion-collage-01&orientation=portrait"
                  alt="CSSBuy Spreadsheet fashion finds collage - sneakers, streetwear, and accessories"
                  title="CSSBuy Spreadsheet Fashion Finds"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl bg-accent-100/60 -z-10" />
              <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl bg-secondary-100/60 -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}